/**
 * People Leader Hub Tour Steps
 * Guides People Leaders through their team management dashboard
 */

import { DemoStep, clickTabAndWait, scrollAndHighlight, refreshDriver } from './tourHelpers'

export const managerJourneyDemoSteps: DemoStep[] = [
  // Step 1: Welcome
  {
    element: 'body',
    popover: {
      title: '🎯 Welcome to the People Leader Hub',
      description: 'This tour will guide you through your complete People Leader toolkit — from team oversight and objective cascading, to conducting 1:1 check-ins, calibrating indicators, managing talent, and supporting your team\'s growth. Let\'s explore!',
      side: 'bottom',
      align: 'center'
    }
  },

  // Step 2: People Leader Header
  {
    element: '[data-tour="manager-header"]',
    popover: {
      title: '📊 Your Command Centre',
      description: 'Your dashboard header shows team size, pending actions requiring your attention, and upcoming check-in sessions. Use this to quickly identify what needs your focus today.',
      side: 'bottom',
      align: 'center'
    }
  },

  // Step 3: Team Overview Tab
  {
    element: '[data-demo="tab-overview"]',
    popover: {
      title: '👥 Team Overview',
      description: 'Get a real-time view of your entire team. Each team member card shows their objective completion status, upcoming check-ins, impact & growth trends, and any alerts requiring attention. Click \'View Details\' on any card to access their full impact & growth workspace.',
      side: 'bottom',
      align: 'center'
    },
    onHighlighted: () => {
      clickTabAndWait('[data-demo="tab-overview"]')
    }
  },

  // Step 4: Team Member Card
  {
    element: '[data-demo="team-member-card"]',
    popover: {
      title: '👤 Team Member Cards',
      description: 'Each card is a snapshot of a team member\'s impact & growth. You\'ll see their objective progress, next scheduled check-in, trend indicators, and alert badges. The \'View Details\' button opens a comprehensive 7-tab workspace where you can review KPAs, impact portfolio, check-ins, and more.',
      side: 'right'
    },
    onHighlighted: () => {
      setTimeout(() => {
        const el = document.querySelector('[data-demo="team-member-card"]') as HTMLElement
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' })
          // Also try scrolling document for nested scroll containers
          const rect = el.getBoundingClientRect()
          const scrollTop = window.pageYOffset || document.documentElement.scrollTop
          const targetY = scrollTop + rect.top - 200
          document.documentElement.scrollTo({ top: targetY, behavior: 'smooth' })
          document.body.scrollTo({ top: targetY, behavior: 'smooth' })
          setTimeout(refreshDriver, 400)
        }
      }, 200)
    }
  },

  // Step 5: Objective Configuration Tab
  {
    element: '[data-demo="tab-objectives"]',
    popover: {
      title: '🎯 Objective Configuration',
      description: 'Manage your team\'s objectives and ensure strategic alignment. From here you can cascade organisational strategic objectives down to team members, review and approve submitted objectives, and monitor alignment across your function.',
      side: 'bottom',
      align: 'center'
    },
    onHighlighted: () => {
      clickTabAndWait('[data-demo="tab-objectives"]')
    }
  },

  // Step 6: Strategic Cascade Visualization
  {
    element: '[data-demo="cascade-viz"]',
    popover: {
      title: '🔄 Strategic Cascade',
      description: 'Visualise how work flows from individual KPIs up through Individual Objectives, into Key Performance Areas, then to Strategic Objectives, and finally to the four Strategic Pillars (Scale, Quality, Culture, Impact). This ensures every team member understands how their work contributes to institutional success.',
      side: 'left'
    },
    onHighlighted: () => {
      // Ensure objectives tab is active when navigating back to this step
      clickTabAndWait('[data-demo="tab-objectives"]')
    }
  },

  // Step 7: Check-In Management Tab
  {
    element: '[data-demo="tab-reviews"]',
    popover: {
      title: '📋 Check-In Management',
      description: 'Schedule and conduct all check-in types for your team: Formal 1:1 check-ins (Mid-Year in June, Year-End in December), Informal check-ins (Q1 in March, Q3 in September), and Special Circumstances check-ins. Use \'Initialize Review Cycles\' to set up the full year\'s check-in calendar, then schedule individual sessions with date, time, and location.',
      side: 'bottom',
      align: 'center'
    },
    onHighlighted: () => {
      clickTabAndWait('[data-demo="tab-reviews"]')
    }
  },

  // Step 8: Calibration Tab (NEW)
  {
    element: '[data-demo="tab-calibration"]',
    popover: {
      title: '⚖️ Calibration',
      description: 'Ensure fairness and consistency across your team and function. In calibration sessions, you meet with peer People Leaders to review proposed indicators, discuss impact portfolios, and reach consensus on final impact & growth indicators. This prevents indicator inflation and ensures equitable outcomes.',
      side: 'bottom',
      align: 'center'
    },
    onHighlighted: () => {
      clickTabAndWait('[data-demo="tab-calibration"]')
    }
  },

  // Step 9: Talent Review Tab (NEW)
  {
    element: '[data-demo="tab-talent"]',
    popover: {
      title: '🌟 Talent Review',
      description: 'Strategic workforce planning starts here. Map your team on the 9-Box Grid (impact & growth vs potential), identify high-potential team members for accelerated growth, plan succession for critical roles, and make data-driven decisions about talent investments.',
      side: 'bottom',
      align: 'center'
    },
    onHighlighted: () => {
      clickTabAndWait('[data-demo="tab-talent"]')
    }
  },

  // Step 10: Team Development Tab
  {
    element: '[data-demo="tab-development"]',
    popover: {
      title: '🌱 Team Growth Plans',
      description: 'Support your team\'s growth by reviewing and approving their Growth Plans, allocating learning resources, and tracking progress on growth actions. Insights from check-ins and calibration flow directly into growth recommendations.',
      side: 'bottom',
      align: 'center'
    },
    onHighlighted: () => {
      clickTabAndWait('[data-demo="tab-development"]')
    }
  },

  // Step 11: Team Analytics Tab
  {
    element: '[data-demo="tab-analytics"]',
    popover: {
      title: '📊 Team Analytics',
      description: 'AI-powered analytics dashboard with impact & growth trends, objective completion rates, and predictive insights. Identify risk factors early, see which team members may need support, and get data-driven recommendations for improving team impact & growth.',
      side: 'bottom',
      align: 'center'
    },
    onHighlighted: () => {
      clickTabAndWait('[data-demo="tab-analytics"]')
    }
  },

  // Step 12: Comprehensive Member View
  {
    element: '[data-demo="view-member-button"]',
    popover: {
      title: '🔍 Comprehensive Member View',
      description: 'Click \'View Details\' on any team member to open their full impact & growth workspace — a 7-tab view covering KPA Overview, Individual Objectives, Underlying KPIs, Impact Portfolio, On-Demand Check-ins, Collaboration, and Growth. Everything you need for a complete picture in one place.',
      side: 'right'
    },
    onHighlighted: () => {
      // Switch back to Team Overview tab first so the member card is visible
      clickTabAndWait('[data-demo="tab-overview"]')
    }
  },

  // Step 13: Tour Complete
  {
    element: 'body',
    popover: {
      title: '✅ You\'re Ready to Lead!',
      description: 'You\'ve explored all People Leader Hub features — from team oversight to strategic talent management. You can restart this tour anytime using the <strong>Explore</strong> button in the header. Start leading your team with confidence!',
      side: 'bottom',
      align: 'center'
    }
  }
]

/**
 * Script for automated demo narration
 * Used for presentation/demo modes — not shown in interactive tours
 */
export const demoScript = {
  introduction: {
    duration: 15,
    narration: 'Welcome to the Eduvos (impact & growth)+ Platform People Leader Hub. This comprehensive dashboard empowers you to lead your team effectively, from objective setting through 1:1 check-in dialogues to talent growth.'
  },

  teamOverview: {
    duration: 20,
    narration: 'The Team Overview gives you instant visibility into your entire team. You can see who needs support, which check-ins are coming up, and take immediate action on alerts.'
  },

  objectives: {
    duration: 25,
    narration: 'The Objectives tab shows how your team\'s work aligns with organisational strategy. You can see the complete cascade from individual KPIs up through strategic pillars, ensuring everyone understands how their work contributes to institutional success.'
  },

  reviews: {
    duration: 30,
    narration: 'Check-In Management streamlines the entire check-in cycle. Schedule formal 1:1 check-ins, informal check-ins, or special circumstance check-ins. The system guides you through the impact portfolio-based check-in process with integrated KPA discussions.'
  },

  calibration: {
    duration: 25,
    narration: 'Calibration sessions ensure fairness and consistency. Meet with peer People Leaders to review proposed indicators, discuss impact portfolios, and reach consensus on final impact & growth indicators across the organisation.'
  },

  talent: {
    duration: 30,
    narration: 'The Talent Review dashboard supports strategic workforce planning. Use the 9-Box Grid to map impact & growth versus potential, identify high-potential team members, plan succession for critical roles, and make informed growth decisions.'
  },

  development: {
    duration: 20,
    narration: 'Growth Plans let you support growth across your team. Review and approve your team\'s Growth Plans, allocate learning resources, and track progress on growth actions.'
  },

  analytics: {
    duration: 25,
    narration: 'The Analytics dashboard provides AI-powered insights into team impact & growth. See trends, identify risk factors early, and get data-driven recommendations for supporting your team.'
  },

  memberDetails: {
    duration: 35,
    narration: 'The Member Details workspace gives you comprehensive access to an individual\'s complete impact & growth profile. Navigate through their KPAs, review impact portfolios, conduct check-ins, and collaborate in real-time — all from one integrated workspace.'
  },

  conclusion: {
    duration: 15,
    narration: 'The People Leader Hub transforms impact & growth management from an administrative burden into a strategic tool for growing talent and driving institutional excellence. Start exploring these features today to lead your team with confidence.'
  }
}
